/* eslint-disable max-lines */
import { useState } from 'react';
import _ from 'lodash';
import { Flex, Margin } from 'components/base/layout-components';
import styled from 'styled-components';
import Text, { Inline } from 'components/base/Text';
import BoldedValue from 'components/BoldedValue';
import { getColor } from 'common/constants/_colors';
import Icon from 'components/base/Icon';
import { ISSUE_STATES, ISSUE_STATES_DISPLAY_VALUES } from '../pulse.common';
import Dropdown from 'components/base/Dropdown';
import Tooltip from '@material-ui/core/Tooltip';
import SearchInput from 'components/SearchInput';
import * as TrackingService from 'common/services/tracking.service';
import { RoundedSquare } from 'components/Shapes';
import { CSSTransition } from 'react-transition-group';

import { PM_PROVIDERS, TRACKING } from 'common/constants/constants';
import UnmatchedBranchesTooltip from './UnMatchedBranches/UnmatchedBranchesTooltip';
import usePrevious from 'components/hooks/usePrevious';
import { LBButton } from '@linearb/buzz';

const FilterButtonWrapper = styled(Flex)`
  cursor: pointer;
  padding: 0.5rem 1rem;
  min-height: 1.6rem;
  border-radius: 0.2rem;
  align-items: center;
  white-space: nowrap;
  border: 1px solid ${getColor('botticelli')};
  gap: 0.5rem;

  :hover {
    ${({ isSelected }) =>
      isSelected
        ? `
    background: ${getColor('titanWhite')};
    `
        : `
    background: ${getColor('zircon')};
    `}
  }

  ${({ isSelected }) =>
    isSelected
      ? `
    background: ${getColor('titanWhite')};
    `
      : ''}
`;

const BorderAnimationWrapper = styled(Flex)`
  ${({ color }) => `
      &.borderAnimation-enter {
        border: 4px solid ${getColor(color, 0.5)};
      }
      &.borderAnimation-enter-active {
        border-width: 0;
        transition: border-width 800ms linear;
      }
      &.borderAnimation-exit {
        border: 4px solid ${getColor(color, 0.5)};
      }
      &.borderAnimation-exit-active {
        border-width: 0;
        transition: border-width 800ms linear;
      }
    `}
`;

const InformationIconWrapper = styled.div`
  border-radius: 50%;
  cursor: pointer;
`;

const Separator = styled(Inline)`
  color: ${getColor('pigeonPost')};
  padding: 0 0.6rem;
  font-size: 1.7rem;
  font-weight: 200;
`;
export const FilterButton = ({
  color,
  name,
  amount = '',
  isSelected,
  onClick
}) => {
  const [isHover, setIsHover] = useState(false);
  const isHighlighted = isSelected || isHover;

  const textColor = isSelected
    ? 'chambray'
    : isHover
    ? 'dodgerBlue'
    : 'shuttleGray';
  return (
    <FilterButtonWrapper
      onClick={onClick}
      isSelected={isSelected}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <RoundedSquare color={isHighlighted ? color : 'pigeonPost'} size={1} />
      <BoldedValue
        cursor='pointer'
        color={textColor}
        pl='x-small'
        value={{ size: 'medium', text: amount }}
        description={{ size: 'medium', text: name }}
      />
    </FilterButtonWrapper>
  );
};

const BorderColorAnimation = ({
  color = 'botticelli',
  on = false,
  dataCy = '',
  children,
  ...extraProps
}) => {
  return (
    <CSSTransition
      timeout={800}
      classNames='borderAnimation'
      in={on}
      data-cy={dataCy}
    >
      <BorderAnimationWrapper color={color} {...extraProps}>
        {children}
      </BorderAnimationWrapper>
    </CSSTransition>
  );
};

const IterationSection = styled(Flex)`
  height: 100%;
  align-items: center;
`;

const PulseTopBar = ({
  iteration,
  wip = 0,
  toDo = 0,
  done = 0,
  issueStateFilters,
  setIssueStateFilters,
  issueTypes = [],
  setIssueTypeFilters,
  setSearchFilterValue,
  searchFilterValue,
  teamName,
  unmatchedBranches,
  allowFilterAnimations = false,
  setUnmatchedBranchesOpen,
  sensor_id = PM_PROVIDERS.JIRA,
  setScrollIntoUnmatchedBranches
}) => {
  const previousFiltersState = usePrevious({ wip, done, toDo });

  if (!iteration) {
    return null;
  }
  const onIssueStateClick = issueState => {
    const setAndLogIssueStates = issueStates => {
      issueStates.length &&
        TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
          page: TRACKING.PAGES.PULSE,
          filter_name: TRACKING.FILTER.ISSUE_STATE,
          filter_value: issueStates
            .map(state => ISSUE_STATES_DISPLAY_VALUES[state].text)
            .join('+'),
          team_name: teamName
        });
      setIssueStateFilters(issueStates);
    };
    const index = issueStateFilters.indexOf(issueState);
    if (index > -1) {
      setAndLogIssueStates(_.without(issueStateFilters, issueState));
    } else {
      setAndLogIssueStates([...issueStateFilters, issueState]);
    }
  };

  const { range, name } = iteration;
  const mapAllTypesIconByPmProvider = {
    [PM_PROVIDERS.JIRA]: 'Jira',
    [PM_PROVIDERS.CLUBHOUSE]: 'Shortcut',
    [PM_PROVIDERS.AZURE_BOARDS]: 'AzureBoards'
  };
  const allTypesIcon = mapAllTypesIconByPmProvider[sensor_id];

  const dropDownOptions = issueTypes.map(({ type, icon }) => ({
    value: type,
    displayValue: (
      <Flex items='center'>
        {icon && <img src={icon} width={14} height={14} alt='' />}
        <Text color='shark' pl='0.5rem' size='medium'>
          {_.startCase(type)}
        </Text>
      </Flex>
    )
  }));
  dropDownOptions.unshift({
    value: 'all',
    displayValue: (
      <Flex items='center'>
        {allTypesIcon && <Icon color='dodgerBlue' name={allTypesIcon} />}
        <Text color='shark' pl='0.5rem' size='medium'>
          All Types
        </Text>
      </Flex>
    )
  });
  const dropdownRenderSelected = values => {
    if (values.length === dropDownOptions.length) {
      return 'All Types';
    }
    return values.length === 1 ? (
      (dropDownOptions.find(({ value }) => value === values[0]) || {})
        .displayValue
    ) : (
      <Tooltip title={values.join(', ')}>
        <Flex items='center'>
          <Icon color='dodgerBlue' name={allTypesIcon} />
          <Text color='waikawaGray' pl='0.5rem' size='medium'>
            {values.length} Types
          </Text>
        </Flex>
      </Tooltip>
    );
  };
  return (
    <Flex
      direction='row'
      pl='medium'
      pr='small'
      pt='small'
      pb='small'
      items='center'
      justify='space-between'
      data-cy='pulse-top-bar'
    >
      <Flex items='center' pt='small' pb='small'>
        <IterationSection>
          <Flex direction='row' items='center'>
            <Text size='x-large' pr='0.8rem'>
              {name}
            </Text>
            <Text size='medium' color='shuttleGray'>
              {range}
            </Text>
            <Separator>|</Separator>
          </Flex>
        </IterationSection>

        <Flex>
          <Flex items='center' ml='small'>
            <BorderColorAnimation
              mr='small'
              color='dodgerBlue'
              on={
                wip > _.get(previousFiltersState, 'wip', 0) &&
                allowFilterAnimations
              }
              dataCy='wip-filter-button'
            >
              <FilterButton
                name={
                  ISSUE_STATES_DISPLAY_VALUES[ISSUE_STATES.IN_PROGRESS].text
                }
                amount={wip}
                color='dodgerBlue'
                onClick={() => {
                  onIssueStateClick(ISSUE_STATES.IN_PROGRESS);
                  setUnmatchedBranchesOpen(false);
                }}
                isSelected={issueStateFilters.includes(
                  ISSUE_STATES.IN_PROGRESS
                )}
              />
            </BorderColorAnimation>
            <BorderColorAnimation
              mr='small'
              color='nepal'
              on={
                toDo > _.get(previousFiltersState, 'toDo', 0) &&
                allowFilterAnimations
              }
              dataCy='todo-filter-button'
            >
              <FilterButton
                name={ISSUE_STATES_DISPLAY_VALUES[ISSUE_STATES.INITIATED].text}
                amount={toDo}
                color='nepal'
                onClick={() => {
                  onIssueStateClick(ISSUE_STATES.INITIATED);
                  setUnmatchedBranchesOpen(false);
                }}
                isSelected={issueStateFilters.includes(ISSUE_STATES.INITIATED)}
              />
            </BorderColorAnimation>
            <BorderColorAnimation
              mr='small'
              color='funGreen'
              on={
                done > _.get(previousFiltersState, 'done', 0) &&
                allowFilterAnimations
              }
              dataCy='done-filter-button'
            >
              <FilterButton
                name={ISSUE_STATES_DISPLAY_VALUES[ISSUE_STATES.DONE].text}
                amount={done}
                color='funGreen'
                onClick={() => {
                  onIssueStateClick(ISSUE_STATES.DONE);
                  setUnmatchedBranchesOpen(false);
                }}
                isSelected={issueStateFilters.includes(ISSUE_STATES.DONE)}
              />
            </BorderColorAnimation>
          </Flex>
          <Flex items='center'>
            <Margin r='small'>
              <Dropdown
                multiple
                options={dropDownOptions}
                renderSelected={dropdownRenderSelected}
                onChange={values => {
                  setIssueTypeFilters(values);
                  if (
                    values.length &&
                    values.length !== dropDownOptions.length - 1
                  ) {
                    TrackingService.logEvent(TRACKING.EVENTS.FILTER, {
                      page: TRACKING.PAGES.PULSE,
                      filter_name: TRACKING.FILTER.ISSUE_TYPE,
                      filter_value: values.join('+'),
                      team_name: teamName
                    });
                  }
                }}
                placeholder='All Types'
                width='auto'
                dataCy='issues-types-dropdown-filter'
              />
            </Margin>
            <SearchInput
              value={searchFilterValue}
              placeholder='search'
              width='10.8rem'
              height='2.6rem'
              onChange={setSearchFilterValue}
              dataCy='issues-search-input'
            />
          </Flex>
        </Flex>
      </Flex>
      <div className='flex flex-row gap-1'>
        {!!unmatchedBranches && (
          <LBButton
            onClick={() => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.PULSE,
                click_type: TRACKING.CLICK.UNMATCHED_BRANCHES
              });
              setUnmatchedBranchesOpen(true);
              setScrollIntoUnmatchedBranches(true);
            }}
          >
            <div
              className='flex flex-row items-center gap-1'
              data-cy='not-linked-branches-text'
            >
              {unmatchedBranches}
              {` ${
                unmatchedBranches === 1
                  ? 'Branch is not linked to issues'
                  : 'Branches are not linked to issues'
              }`}
              <UnmatchedBranchesTooltip>
                <InformationIconWrapper>
                  <Icon name='infooutlined' color='white' size='smaller' />
                </InformationIconWrapper>
              </UnmatchedBranchesTooltip>
            </div>
          </LBButton>
        )}
      </div>
    </Flex>
  );
};

export default PulseTopBar;
